import { TBoard } from './BoardArrangement';
import BoardUtils from './BoardUtils';

const BoardConnections = {
    getAvailableForBoard(board: TBoard, keys: string[]): string[] {
        return board.reduce((acc, tile) => {
            if (this.checkAvailable(tile.locId, tile.blockPositions, keys)) {
                acc.push(tile.boardId);
            }
            return acc;
        }, []);
    },

    checkAvailable(checkId: string, blockIds: string[], keys: string[]): boolean {
        const found = blockIds.filter(b => keys.includes(b));
        let allSame = false;
        const foundAbove = found.filter(t => {
            const nums = BoardUtils.locFromId(checkId);
            const tnums = BoardUtils.locFromId(t);
            if (tnums[0] !== nums[0]) {
                return t;
            } else {
                return null;
            }
        });

        if (found.length > 1 && foundAbove.length === 0) {
            allSame = this.allSameSide(found, checkId);
        }

        return (
            found.length === 0 ||
            allSame ||
            (found.length === 1 && this.checkSides(found[0], checkId))
        );
    },

    allSameSide(found: string[], checkId: string) {
        const allSides: string[] = this.findMatchingSides(found, checkId);
        if (allSides.length === 0) {
            return false;
        }
        const nums = BoardUtils.locFromId(checkId);
        const found0 = BoardUtils.locFromId(allSides[0]);
        const sideOffset = nums[1] - found0[1];
        const oppositeSides = allSides.reduce((acc, f) => {
            const n = BoardUtils.locFromId(f);
            if (n[0] !== nums[0]) {
                acc++;
            }
            if (nums[1] - n[1] !== sideOffset) {
                acc++;
            }
            return acc;
        }, 0);

        return oppositeSides === 0;
    },

    findMatchingSides(found: string[], checkId: string): string[] {
        const foundSides: string[] = [];
        const nums = BoardUtils.locFromId(checkId);
        const sideA = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2]]);
        if (found.indexOf(sideA) !== -1) {
            foundSides.push(sideA);
        }
        const sideA1 = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2] - 0.5]);
        if (found.indexOf(sideA1) !== -1) {
            foundSides.push(sideA1);
        }
        const sideA2 = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2] + 0.5]);
        if (found.indexOf(sideA2) !== -1) {
            foundSides.push(sideA2);
        }
        const sideB = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2]]);
        if (found.indexOf(sideB) !== -1) {
            foundSides.push(sideB);
        }
        const sideB1 = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2] - 0.5]);
        if (found.indexOf(sideB1) !== -1) {
            foundSides.push(sideB1);
        }
        const sideB2 = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2] + 0.5]);
        if (found.indexOf(sideB2) !== -1) {
            foundSides.push(sideB2);
        }
        return foundSides;
    },

    // check if position is blocked on by sides
    checkSides(found: string, checkId: string) {
        const nums = BoardUtils.locFromId(checkId);
        const sideA = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2]]);
        const sideA1 = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2] - 0.5]);
        const sideA2 = BoardUtils.formatBoardId([nums[0], nums[1] + 1, nums[2] + 0.5]);
        const sideB = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2]]);
        const sideB1 = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2] - 0.5]);
        const sideB2 = BoardUtils.formatBoardId([nums[0], nums[1] - 1, nums[2] + 0.5]);
        return (
            sideA === found ||
            sideB === found ||
            sideA1 === found ||
            sideA2 === found ||
            sideB1 === found ||
            sideB2 === found
        );
    },

    // return the positions that would block the current one
    calculateBlockPositions(position: [number, number, number], totalLayers: number) {
        // add optional param here for size...
        // can add more blockers for bigger hidden objects..
        let blockers: string[] = this.getConnectSameLayer(position);
        blockers.push(BoardUtils.formatBoardId([position[0] + 1, position[1], position[2]]));
        let numAbove: number = totalLayers > position[0] ? totalLayers - position[0] : 0;
        let z = position[0];
        if (numAbove > 0) {
            while (numAbove > 0) {
                const blocksAbove = this.getConnectOtherLayers([z + 1, position[1], position[2]]);
                blockers = [...blockers, ...blocksAbove];
                z++;
                numAbove--;
            }
        }

        return blockers;
    },

    // returns the positions that would hold this one above it
    calculateSupportPositions(position: [number, number, number]) {
        // straight below or corners
        if (position[0] === 0) {
            return ['FLOOR'];
        }

        return this.getConnectOtherLayers([position[0] - 1, position[1], position[2]]);
    },

    // returns positions that surround but do not overlap position
    getConnectSameLayer(position: [number, number, number]): string[] {
        const b: string[] = [];
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 1, position[2]]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 1, position[2]]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 1, position[2] - 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 1, position[2] - 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 1, position[2] + 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 1, position[2] + 0.5]));
        return b;
    },

    // returns all positions connected with current position, including ones that would overlap if in same layer
    getConnectOtherLayers(position: [number, number, number]): string[] {
        const b: string[] = [];
        b.push(BoardUtils.formatBoardId([position[0], position[1], position[2]]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 0.5, position[2]]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 0.5, position[2]]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 0.5, position[2] - 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] - 0.5, position[2] + 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 0.5, position[2] - 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1] + 0.5, position[2] + 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1], position[2] - 0.5]));
        b.push(BoardUtils.formatBoardId([position[0], position[1], position[2] + 0.5]));

        return b;
    },
};

export default BoardConnections;
